/**
 * Correlation ranking utilities for the Correlogram column selector.
 *
 * Provides three relationship detectors (linear, quadratic, exponential)
 * and a column-level "interestingness" ranking that aggregates pairwise
 * scores to pick the best N columns to display.
 */

import { pearsonCorrelation, partialCorrelation, multipleCorrelationR } from "./useHistogramBins";

export type RelationshipType = "linear" | "quadratic" | "exponential";

export interface ColumnDef {
  field: string;
  headerName?: string;
}

// ---------------------------------------------------------------------------
// Constant-column detection
// ---------------------------------------------------------------------------

function extractFinite(rows: Record<string, unknown>[], key: string): number[] {
  const out: number[] = [];
  for (const r of rows) {
    const v = r[key];
    if (typeof v === "number" && Number.isFinite(v)) out.push(v);
  }
  return out;
}

export function isConstantColumn(rows: Record<string, unknown>[], key: string): boolean {
  const vals = extractFinite(rows, key);
  if (vals.length < 2) return true;
  const first = vals[0]!;
  return vals.every(v => v === first);
}

export function filterConstantColumns(
  rows: Record<string, unknown>[],
  columns: ColumnDef[],
): ColumnDef[] {
  return columns.filter(c => !isConstantColumn(rows, c.field));
}

// ---------------------------------------------------------------------------
// Pairwise relationship scoring
// ---------------------------------------------------------------------------

function extractAlignedPairs(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
): { x: number[]; y: number[] } {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const r of rows) {
    const xv = r[xKey];
    const yv = r[yKey];
    if (
      typeof xv === "number" && Number.isFinite(xv) &&
      typeof yv === "number" && Number.isFinite(yv)
    ) {
      xs.push(xv);
      ys.push(yv);
    }
  }
  return { x: xs, y: ys };
}

/** R^2 of a linear fit (Pearson^2). */
function linearR2(x: number[], y: number[]): number {
  const r = pearsonCorrelation(x, y);
  return Number.isFinite(r) ? r * r : 0;
}

/**
 * R^2 of a quadratic fit y = a + bx + cx^2.
 * Solves the 3x3 normal equations (Vandermonde).
 */
function quadraticR2(x: number[], y: number[]): number {
  const n = x.length;
  if (n < 4) return 0;

  let sx = 0, sx2 = 0, sx3 = 0, sx4 = 0;
  let sy = 0, sxy = 0, sx2y = 0;
  for (let i = 0; i < n; i++) {
    const xi = x[i]!;
    const yi = y[i]!;
    const x2 = xi * xi;
    sx += xi;
    sx2 += x2;
    sx3 += x2 * xi;
    sx4 += x2 * x2;
    sy += yi;
    sxy += xi * yi;
    sx2y += x2 * yi;
  }

  // Normal equations: M * [a, b, c]^T = rhs
  // M = [[n, sx, sx2], [sx, sx2, sx3], [sx2, sx3, sx4]]
  // Solve via Cramer's rule
  const det =
    n * (sx2 * sx4 - sx3 * sx3) -
    sx * (sx * sx4 - sx3 * sx2) +
    sx2 * (sx * sx3 - sx2 * sx2);

  if (Math.abs(det) < 1e-30) return 0;

  const a = (
    sy * (sx2 * sx4 - sx3 * sx3) -
    sx * (sxy * sx4 - sx2y * sx3) +
    sx2 * (sxy * sx3 - sx2y * sx2)
  ) / det;

  const b = (
    n * (sxy * sx4 - sx2y * sx3) -
    sy * (sx * sx4 - sx3 * sx2) +
    sx2 * (sx * sx2y - sxy * sx2)
  ) / det;

  const c = (
    n * (sx2 * sx2y - sx3 * sxy) -
    sx * (sx * sx2y - sxy * sx2) +
    sy * (sx * sx3 - sx2 * sx2)
  ) / det;

  const yMean = sy / n;
  let ssRes = 0, ssTot = 0;
  for (let i = 0; i < n; i++) {
    const xi = x[i]!;
    const yi = y[i]!;
    const pred = a + b * xi + c * xi * xi;
    ssRes += (yi - pred) ** 2;
    ssTot += (yi - yMean) ** 2;
  }

  return ssTot > 0 ? Math.max(0, 1 - ssRes / ssTot) : 0;
}

/**
 * R^2 of an exponential fit y = a * e^(bx).
 * Linearized: ln(y) = ln(a) + bx, using only positive y values.
 */
function exponentialR2(x: number[], y: number[]): number {
  const xf: number[] = [];
  const lny: number[] = [];
  for (let i = 0; i < x.length; i++) {
    if (y[i]! > 0) {
      xf.push(x[i]!);
      lny.push(Math.log(y[i]!));
    }
  }
  if (xf.length < 3) return 0;
  const r = pearsonCorrelation(xf, lny);
  return Number.isFinite(r) ? r * r : 0;
}

const SCORERS: Record<RelationshipType, (x: number[], y: number[]) => number> = {
  linear: linearR2,
  quadratic: quadraticR2,
  exponential: exponentialR2,
};

// ---------------------------------------------------------------------------
// Column interestingness ranking
// ---------------------------------------------------------------------------

/**
 * Rank columns by aggregate pairwise relationship strength.
 *
 * For each pair of columns, scores the relationship using the chosen type.
 * Each column accumulates its best pairwise score (max, not mean — we want
 * columns that participate in at least one strong relationship).
 *
 * Returns column keys sorted by descending interestingness.
 */
export function rankColumns(
  rows: Record<string, unknown>[],
  columns: ColumnDef[],
  type: RelationshipType,
): string[] {
  const scorer = SCORERS[type];
  const scores = new Map<string, number>();

  for (const col of columns) scores.set(col.field, 0);

  for (let i = 0; i < columns.length; i++) {
    for (let j = i + 1; j < columns.length; j++) {
      const a = columns[i]!;
      const b = columns[j]!;
      const { x, y } = extractAlignedPairs(rows, a.field, b.field);
      if (x.length < 3) continue;

      const score = scorer(x, y);
      scores.set(a.field, Math.max(scores.get(a.field)!, score));
      scores.set(b.field, Math.max(scores.get(b.field)!, score));
    }
  }

  return columns
    .map(c => c.field)
    .sort((a, b) => scores.get(b)! - scores.get(a)!);
}

/**
 * Pick N random columns (Fisher-Yates on a copy).
 */
export function randomColumns(columns: ColumnDef[], n: number): string[] {
  const shuffled = columns.map(c => c.field);
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return shuffled.slice(0, Math.min(n, shuffled.length));
}

// ---------------------------------------------------------------------------
// 3D surface interestingness — Z-axis ranking
// ---------------------------------------------------------------------------

/**
 * Extract aligned triples (x, y, z) where all three are finite.
 */
function extractAlignedTriples(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
  zKey: string,
): { x: number[]; y: number[]; z: number[] } {
  const xs: number[] = [];
  const ys: number[] = [];
  const zs: number[] = [];
  for (const r of rows) {
    const xv = r[xKey], yv = r[yKey], zv = r[zKey];
    if (
      typeof xv === "number" && Number.isFinite(xv) &&
      typeof yv === "number" && Number.isFinite(yv) &&
      typeof zv === "number" && Number.isFinite(zv)
    ) {
      xs.push(xv);
      ys.push(yv);
      zs.push(zv);
    }
  }
  return { x: xs, y: ys, z: zs };
}

/**
 * Score a single Z candidate against one (X, Y) pair.
 *
 * Four signals combined:
 *  1. Multiple R — how well X+Z together predict Y (and vice versa)
 *  2. Partial correlation shift — does Z restructure the X-Y relationship?
 *  3. Non-redundancy — is Z independent from X and Y?
 *  4. Nonlinear surface gain — does Z add curvature?
 */
function scoreZCandidate(
  x: number[],
  y: number[],
  z: number[],
): number {
  if (x.length < 4) return 0;

  // Signal 1: Multiple R (best of predicting Y from X,Z or X from Y,Z)
  const multiR1 = multipleCorrelationR(x, y, z);
  const multiR2 = multipleCorrelationR(y, x, z);
  const multiRScore = Math.max(
    Number.isFinite(multiR1) ? multiR1 : 0,
    Number.isFinite(multiR2) ? multiR2 : 0,
  );

  // Signal 2: Partial correlation shift — |r_xy| vs |r_xy.z|
  const rxy = pearsonCorrelation(x, y);
  const rxyPartial = partialCorrelation(x, y, z);
  const partialShift = (Number.isFinite(rxy) && Number.isFinite(rxyPartial))
    ? Math.abs(Math.abs(rxy) - Math.abs(rxyPartial))
    : 0;

  // Signal 3: Non-redundancy — Z not collinear with X or Y
  const rxz = pearsonCorrelation(x, z);
  const ryz = pearsonCorrelation(y, z);
  const rxz2 = Number.isFinite(rxz) ? rxz * rxz : 0;
  const ryz2 = Number.isFinite(ryz) ? ryz * ryz : 0;
  const nonRedundancy = (1 - rxz2) * (1 - ryz2);

  // Signal 4: Nonlinear gain — quadratic R² minus linear R² for Z vs X and Z vs Y
  const nlGainXZ = Math.max(0, quadraticR2(x, z) - linearR2(x, z));
  const nlGainYZ = Math.max(0, quadraticR2(y, z) - linearR2(y, z));
  const nonlinearGain = Math.max(nlGainXZ, nlGainYZ);

  return (
    0.35 * multiRScore +
    0.25 * partialShift +
    0.25 * nonRedundancy +
    0.15 * nonlinearGain
  );
}

export interface ZCandidateScore {
  field: string;
  score: number;
  label?: string;
}

/**
 * Rank Z-axis candidates for a single (X, Y) pair.
 *
 * Returns candidates sorted by descending "surface interestingness" —
 * how much 3D structure Z adds beyond the 2D X-Y scatter.
 */
export function rankZForPair(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
  candidates: ColumnDef[],
): ZCandidateScore[] {
  const results: ZCandidateScore[] = [];
  for (const c of candidates) {
    if (c.field === xKey || c.field === yKey) continue;
    const { x, y, z } = extractAlignedTriples(rows, xKey, yKey, c.field);
    const score = scoreZCandidate(x, y, z);
    results.push({ field: c.field, score, label: c.headerName ?? c.field });
  }
  return results.sort((a, b) => b.score - a.score);
}

/**
 * Rank Z-axis candidates across all selected column pairs.
 *
 * Strategy: find the "dominant pair" (highest pairwise R²) among the
 * selected columns, then rank Z candidates against that pair.
 * Falls back to averaging across all pairs if no clear dominant pair.
 *
 * Returns candidates sorted by descending surface interestingness.
 */
export function rankZCandidates(
  rows: Record<string, unknown>[],
  selectedColumns: ColumnDef[],
  allCandidates: ColumnDef[],
): ZCandidateScore[] {
  if (selectedColumns.length < 2) return [];

  // Find the dominant pair — the pair with the highest linear R²
  let bestR2 = -1;
  let bestXKey = selectedColumns[0]!.field;
  let bestYKey = selectedColumns[1]!.field;

  for (let i = 0; i < selectedColumns.length; i++) {
    for (let j = i + 1; j < selectedColumns.length; j++) {
      const { x, y } = extractAlignedPairs(rows, selectedColumns[i]!.field, selectedColumns[j]!.field);
      if (x.length < 3) continue;
      const r2 = linearR2(x, y);
      if (r2 > bestR2) {
        bestR2 = r2;
        bestXKey = selectedColumns[i]!.field;
        bestYKey = selectedColumns[j]!.field;
      }
    }
  }

  // If the dominant pair is weak, average across top 3 pairs instead
  const selectedFields = new Set(selectedColumns.map(c => c.field));
  if (bestR2 < 0.1 && selectedColumns.length >= 4) {
    // Gather top pairs
    const pairs: { xKey: string; yKey: string; r2: number }[] = [];
    for (let i = 0; i < selectedColumns.length; i++) {
      for (let j = i + 1; j < selectedColumns.length; j++) {
        const { x, y } = extractAlignedPairs(rows, selectedColumns[i]!.field, selectedColumns[j]!.field);
        if (x.length < 3) continue;
        pairs.push({ xKey: selectedColumns[i]!.field, yKey: selectedColumns[j]!.field, r2: linearR2(x, y) });
      }
    }
    pairs.sort((a, b) => b.r2 - a.r2);
    const topPairs = pairs.slice(0, 3);

    // Average Z scores across top pairs
    const scoreMap = new Map<string, { total: number; count: number; label?: string }>();
    for (const pair of topPairs) {
      const ranked = rankZForPair(rows, pair.xKey, pair.yKey, allCandidates);
      for (const r of ranked) {
        if (selectedFields.has(r.field)) continue; // don't suggest an already-plotted column
        const entry = scoreMap.get(r.field) ?? { total: 0, count: 0, label: r.label };
        entry.total += r.score;
        entry.count += 1;
        scoreMap.set(r.field, entry);
      }
    }
    return [...scoreMap.entries()]
      .map(([field, { total, count, label }]) => ({ field, score: total / count, label }))
      .sort((a, b) => b.score - a.score);
  }

  // Dominant pair: rank candidates against it
  return rankZForPair(rows, bestXKey, bestYKey, allCandidates)
    .filter(r => !selectedFields.has(r.field));
}

/**
 * Score a specific (X, Y, Z) triplet for surface interestingness.
 * Useful for per-cell badges in the correlogram grid.
 */
export function surfaceScore(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
  zKey: string,
): number {
  const { x, y, z } = extractAlignedTriples(rows, xKey, yKey, zKey);
  return scoreZCandidate(x, y, z);
}
